import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Progress,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import {
  MdClose,
  MdCode,
  MdDescription,
  MdDownload,
  MdEmail,
  MdImage,
  MdInsertDriveFile,
  MdLayers,
  MdNotes,
  MdSlideshow,
  MdTableChart,
  MdUploadFile,
  MdWeb,
} from 'react-icons/md';
import type { NormalizedEmail } from '../../utils/parseEml';
import { detectFileCategory, humanCategoryLabel, type ConversionCategory, type DetectedFile } from '../../utils/detectFileCategory';
import { convertUniversalToPdf, type UniversalConvertBundle } from '../../utils/convertUniversal';
import { applyPdfPassword } from '../../utils/pdfPassword';
import { triggerPdfDownload } from '../../utils/convertEmlToPdf';
import { parseEml } from '../../utils/parseEml';
import { parseMsgToNormalizedEmail } from '../../utils/msgToNormalizedEmail';
import { listSpreadsheetSheetNames } from '../../utils/convertSpreadsheetToPdf';
import { SupportedFormatsSection } from './SupportedFormatsSection';
import { ConvertToPdfSettingsPanel } from './ConvertToPdfSettingsPanel';
import { createDefaultBundle } from './convertToPdfDefaults';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const PAGE_BG = '#FAFAFA';
const CARD_SHADOW = '0 1px 2px rgba(15, 23, 42, 0.05), 0 18px 52px -16px rgba(15, 23, 42, 0.1)';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n >= 10240 ? 0 : 1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function friendlyKindLabel(category: ConversionCategory, extUc: string): string {
  const e = extUc.toLowerCase();
  const map: Record<string, string> = {
    docx: 'Word document',
    rtf: 'Rich Text',
    xlsx: 'Excel spreadsheet',
    xls: 'Excel spreadsheet',
    csv: 'CSV',
    pptx: 'PowerPoint',
    png: 'PNG image',
    jpg: 'JPEG image',
    jpeg: 'JPEG image',
    webp: 'WebP image',
    gif: 'GIF image',
    svg: 'SVG image',
    heic: 'HEIC image',
    eml: 'Email message',
    msg: 'Outlook message',
    md: 'Markdown',
    html: 'HTML',
    htm: 'HTML',
    txt: 'Plain text',
  };
  if (map[e]) return map[e];
  if (category === 'image') return extUc ? `${extUc} image` : 'Image';
  if (category === 'code') return extUc ? `${extUc} file` : 'Code file';
  return humanCategoryLabel(category);
}

function fileKindIcon(category: ConversionCategory): typeof MdInsertDriveFile {
  switch (category) {
    case 'document':
      return MdDescription;
    case 'spreadsheet':
      return MdTableChart;
    case 'presentation':
      return MdSlideshow;
    case 'image':
      return MdImage;
    case 'email':
      return MdEmail;
    case 'html':
      return MdWeb;
    case 'code':
      return MdCode;
    case 'text':
      return MdNotes;
    default:
      return MdInsertDriveFile;
  }
}

function StatusChip({
  unsupported,
  parseError,
  jobError,
  prepProgress,
  ready,
}: {
  unsupported: boolean;
  parseError: boolean;
  jobError: boolean;
  prepProgress: boolean;
  ready: boolean;
}) {
  let label = 'Starting…';
  let bg = 'gray.50';
  let color = 'gray.600';

  if (unsupported) {
    label = 'Unsupported';
    bg = 'orange.50';
    color = 'orange.800';
  } else if (parseError || jobError) {
    label = 'Error';
    bg = 'red.50';
    color = 'red.700';
  } else if (prepProgress) {
    label = 'Preparing…';
    bg = 'purple.50';
    color = 'purple.700';
  } else if (ready) {
    label = 'Ready';
    bg = 'green.50';
    color = 'green.700';
  }

  return (
    <Box
      as="span"
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="600"
      letterSpacing="0.02em"
      bg={bg}
      color={color}
      flexShrink={0}
    >
      {label}
    </Box>
  );
}

export const ConvertToPdfPage: React.FC = () => {
  const toast = useToast({ position: 'bottom-right' });
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const pdfConversionGenRef = useRef(0);

  const [activeFile, setActiveFile] = useState<File | null>(null);
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [detected, setDetected] = useState<DetectedFile | null>(null);
  const [bundle, setBundle] = useState<UniversalConvertBundle>(() => createDefaultBundle('text'));

  const [parsedEmail, setParsedEmail] = useState<NormalizedEmail | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);

  const [imagePx, setImagePx] = useState<{ width: number; height: number } | null>(null);

  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pdfStale, setPdfStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prepProgress, setPrepProgress] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);

  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const emailInitRef = useRef<string | null>(null);

  const category = detected?.category ?? 'unsupported';
  const unsupported = category === 'unsupported';

  const passwordTooShort = passwordEnabled && password.length > 0 && password.length < 6;
  const passwordMismatch = passwordEnabled && confirmPassword.length > 0 && password !== confirmPassword;
  const passwordValid = !passwordEnabled || (password.length >= 6 && password === confirmPassword);

  const resetWorkspace = useCallback(() => {
    setActiveFile(null);
    setBuffer(null);
    setDetected(null);
    setParsedEmail(null);
    setSheetNames([]);
    setImagePx(null);
    setPdfBytes(null);
    setPdfStale(false);
    setJobError(null);
    setBundle(createDefaultBundle('text'));
    emailInitRef.current = null;
  }, []);

  const ingestFile = useCallback(
    async (file: File) => {
      setBusy(true);
      setJobError(null);
      try {
        const det = detectFileCategory(file);
        const buf = await file.arrayBuffer();
        setActiveFile(file);
        setBuffer(buf);
        setDetected(det);
        setBundle(createDefaultBundle(det.category === 'unsupported' ? 'text' : det.category));
        setPdfBytes(null);
        setPdfStale(true);
        setParsedEmail(null);
        setSheetNames([]);
        setImagePx(null);
        emailInitRef.current = null;

        if (det.category === 'unsupported') {
          setJobError(null);
          setBusy(false);
          return;
        }

        if (det.category === 'email') {
          const id = crypto.randomUUID();
          const lower = file.name.toLowerCase();
          const em = lower.endsWith('.msg')
            ? parseMsgToNormalizedEmail(buf, id, file.name)
            : await parseEml(file, id);
          setParsedEmail(em);
        }

        if (det.category === 'spreadsheet') {
          try {
            setSheetNames(listSpreadsheetSheetNames(buf));
          } catch {
            setSheetNames([]);
          }
        }

        if (det.category === 'image') {
          try {
            const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
            setImagePx({ width: bmp.width, height: bmp.height });
            bmp.close();
          } catch {
            setImagePx(null);
          }
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not read file';
        toast({ title: 'Read error', description: msg, status: 'error', duration: 5000 });
        setJobError(msg);
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    if (!parsedEmail || parsedEmail.isParseError || category !== 'email') return;
    if (emailInitRef.current === parsedEmail.id) return;
    emailInitRef.current = parsedEmail.id;
    const ids = parsedEmail.attachments.filter((a) => a.embeddableInPdf).map((a) => a.id);
    setBundle((b) => {
      const email = b.email ?? createDefaultBundle('email').email!;
      return { ...b, email: { ...email, embedAttachmentIds: ids } };
    });
  }, [parsedEmail, category]);

  useEffect(() => {
    if (passwordEnabled && !passwordValid) {
      setPdfBytes(null);
      setPdfStale(true);
    }
  }, [passwordEnabled, passwordValid]);

  useEffect(() => {
    if (!activeFile || !buffer || !detected || unsupported || !passwordValid || busy || parsedEmail?.isParseError)
      return;

    if (category === 'email' && !parsedEmail) return;

    const t = window.setTimeout(() => {
      const generation = ++pdfConversionGenRef.current;
      void (async () => {
        setPrepProgress(true);
        setJobError(null);
        try {
          const raw = await convertUniversalToPdf(activeFile, buffer, detected, bundle, {
            email: parsedEmail,
            imagePx: imagePx ?? undefined,
          });
          let out = raw;
          if (passwordEnabled && passwordValid) {
            out = await applyPdfPassword(raw, password);
          }
          if (generation === pdfConversionGenRef.current) {
            setPdfBytes(out);
            setPdfStale(false);
          }
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Conversion failed';
          if (generation === pdfConversionGenRef.current) {
            setJobError(msg);
            setPdfBytes(null);
            toastRef.current({ title: 'PDF preparation failed', description: msg, status: 'error', duration: 6000 });
          }
        } finally {
          if (generation === pdfConversionGenRef.current) {
            setPrepProgress(false);
          }
        }
      })();
    }, 420);

    return () => {
      window.clearTimeout(t);
    };
  }, [
    activeFile,
    buffer,
    detected,
    unsupported,
    bundle,
    category,
    passwordEnabled,
    password,
    confirmPassword,
    passwordValid,
    parsedEmail,
    imagePx,
    busy,
  ]);

  const canDownload = Boolean(
    activeFile &&
      !unsupported &&
      !parsedEmail?.isParseError &&
      pdfBytes &&
      !pdfStale &&
      !prepProgress &&
      passwordValid &&
      !jobError,
  );

  const readyChip = Boolean(
    pdfBytes && !pdfStale && passwordValid && !jobError && !unsupported && !parsedEmail?.isParseError,
  );

  const onDrop = useCallback(
    (files: File[]) => {
      const f = files[0];
      if (f) void ingestFile(f);
    },
    [ingestFile],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxFiles: 1,
    multiple: false,
    disabled: busy || Boolean(activeFile),
    onDrop,
    noClick: false,
  });

  const downloadPdf = () => {
    if (!pdfBytes || !activeFile) return;
    const base = activeFile.name.replace(/\.[^/.]+$/, '');
    triggerPdfDownload(pdfBytes, `${base}.pdf`);
  };

  const attachments = parsedEmail?.attachments ?? [];

  const uploadPadding = { base: 8, md: 10 };

  const dropProps = getRootProps({
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!busy) open();
      }
    },
  });

  return (
    <Flex direction="column" flex="1" minH="0" minW="0" bg={PAGE_BG}>
      <Box flex="1">
        <VStack align="stretch" spacing={0} maxW="920px" mx="auto" px={{ base: 4, md: 6 }} pt={{ base: 6, md: 8 }} pb={{ base: 12, md: 16 }}>
          {!activeFile && (
            <MotionBox
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <Box
                {...dropProps}
                w="full"
                maxW="560px"
                mx="auto"
                bg={isDragActive ? 'purple.50' : 'white'}
                borderRadius="20px"
                p={uploadPadding}
                cursor={busy ? 'wait' : 'pointer'}
                transition="box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease"
                shadow="sm"
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={isDragActive ? 'purple.400' : 'gray.200'}
                outline="none"
                _hover={
                  busy
                    ? {}
                    : {
                        shadow: 'md',
                        borderColor: 'purple.300',
                        bg: 'purple.50',
                      }
                }
                _focusVisible={{
                  boxShadow: `0 0 0 3px rgba(124, 58, 237, 0.22)`,
                  borderColor: 'purple.400',
                }}
              >
                <input {...getInputProps()} />
                <VStack spacing={4}>
                  <MotionBox animate={isDragActive ? { scale: 1.02 } : { scale: 1 }} transition={{ duration: 0.2 }}>
                    <Flex
                      align="center"
                      justify="center"
                      w="64px"
                      h="64px"
                      borderRadius="18px"
                      bg={isDragActive ? 'purple.100' : 'gray.100'}
                      mx="auto"
                    >
                      <Icon as={MdLayers} boxSize={8} color={isDragActive ? 'purple.500' : 'gray.400'} />
                    </Flex>
                  </MotionBox>
                  <VStack spacing={0.5}>
                    <Text fontWeight="700" fontSize="lg" color="gray.800">
                      Drop file here
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      Drag and drop a file, or click below to browse
                    </Text>
                  </VStack>
                  <Text fontSize="xs" color="gray.400" maxW="sm" textAlign="center" lineHeight="1.5">
                    DOCX, XLSX, PPTX, PNG, JPG, EML, and more
                  </Text>
                  <Button
                    variant="outline"
                    size="sm"
                    borderRadius="10px"
                    borderColor="gray.200"
                    color="gray.700"
                    fontWeight="600"
                    px={6}
                    leftIcon={<MdUploadFile size={17} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                    isLoading={busy}
                    alignSelf="center"
                    _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
                  >
                    Browse file
                  </Button>
                </VStack>
              </Box>
            </MotionBox>
          )}

          {activeFile && detected && (
            <MotionFlex
              direction={{ base: 'column', lg: 'row' }}
              gap={{ base: 8, lg: 10 }}
              align={{ lg: 'flex-start' }}
              justify="center"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box
                flex="1"
                minW={0}
                maxW={{ lg: '440px' }}
                bg="white"
                borderRadius="3xl"
                p={{ base: 6, md: 8 }}
                boxShadow={CARD_SHADOW}
                borderWidth="1px"
                borderColor="gray.100"
              >
                <VStack align="stretch" spacing={6}>
                  <Flex gap={5} align="flex-start">
                    <Flex
                      align="center"
                      justify="center"
                      w={{ base: 14, md: 16 }}
                      h={{ base: 14, md: 16 }}
                      borderRadius="2xl"
                      bg="gray.50"
                      flexShrink={0}
                    >
                      <Icon as={fileKindIcon(category)} boxSize={{ base: 9, md: 10 }} color="gray.500" />
                    </Flex>
                    <Box minW={0} flex={1}>
                      <Text fontWeight="600" fontSize={{ base: 'md', md: 'lg' }} color="gray.900" noOfLines={3} lineHeight="short">
                        {activeFile.name}
                      </Text>
                      <Text fontSize="sm" color="gray.500" mt={2} lineHeight="1.5">
                        {friendlyKindLabel(category, detected.extension)} · {formatBytes(activeFile.size)}
                      </Text>
                    </Box>
                    <StatusChip
                      unsupported={unsupported}
                      parseError={Boolean(parsedEmail?.isParseError)}
                      jobError={Boolean(jobError)}
                      prepProgress={prepProgress}
                      ready={readyChip}
                    />
                  </Flex>

                  {(unsupported || parsedEmail?.isParseError || jobError) && (
                    <Text fontSize="sm" color={jobError || parsedEmail?.isParseError ? 'red.600' : 'orange.700'} lineHeight="1.6">
                      {unsupported && <>This file type isn&apos;t supported for in-browser conversion.</>}
                      {parsedEmail?.isParseError && <>{parsedEmail.errorMessage ?? 'Could not parse this email.'}</>}
                      {jobError && !unsupported && !parsedEmail?.isParseError && <>{jobError}</>}
                    </Text>
                  )}

                  <Button
                    size="sm"
                    variant="ghost"
                    borderRadius="xl"
                    color="gray.600"
                    fontWeight="600"
                    leftIcon={<MdClose size={16} />}
                    onClick={resetWorkspace}
                  >
                    Remove
                  </Button>
                </VStack>
              </Box>

              <Box w={{ base: 'full', lg: '380px' }} flexShrink={0} alignSelf={{ base: 'stretch', lg: 'flex-start' }}>
                <Box
                  bg="white"
                  borderRadius="3xl"
                  p={{ base: 6, md: 7 }}
                  boxShadow={CARD_SHADOW}
                  borderWidth="1px"
                  borderColor="gray.100"
                  position={{ lg: 'sticky' }}
                  top={{ lg: 8 }}
                >
                  <Text fontSize="xs" fontWeight="600" color="gray.400" letterSpacing="0.06em" textTransform="uppercase" mb={5}>
                    Settings
                  </Text>
                  <ConvertToPdfSettingsPanel
                    category={category}
                    bundle={bundle}
                    onBundleChange={(b) => {
                      setBundle(b);
                      setPdfStale(true);
                    }}
                    sheetNames={sheetNames}
                    attachments={attachments}
                    disabled={unsupported || Boolean(parsedEmail?.isParseError)}
                    passwordEnabled={passwordEnabled}
                    password={password}
                    confirmPassword={confirmPassword}
                    showPassword={showPw}
                    showConfirmPassword={showPw2}
                    onPasswordEnabled={(v) => {
                      setPasswordEnabled(v);
                      setPdfStale(true);
                    }}
                    onPassword={(v) => {
                      setPassword(v);
                      setPdfStale(true);
                    }}
                    onConfirmPassword={(v) => {
                      setConfirmPassword(v);
                      setPdfStale(true);
                    }}
                    onShowPassword={setShowPw}
                    onShowConfirmPassword={setShowPw2}
                    passwordMismatch={passwordMismatch}
                    passwordTooShort={passwordTooShort}
                  />

                  {prepProgress && (
                    <Box mt={6}>
                      <Progress size="xs" isIndeterminate colorScheme="purple" borderRadius="full" bg="gray.100" />
                      <Text fontSize="xs" color="gray.400" mt={2} fontWeight="500">
                        Updating PDF…
                      </Text>
                    </Box>
                  )}

                  <Button
                    w="full"
                    mt={6}
                    size="lg"
                    h="52px"
                    borderRadius="xl"
                    bg="gray.900"
                    color="white"
                    fontWeight="600"
                    fontSize="md"
                    leftIcon={<MdDownload size={22} />}
                    _hover={{ bg: 'gray.800' }}
                    _active={{ bg: 'gray.900' }}
                    _disabled={{ bg: 'gray.100', color: 'gray.400', cursor: 'not-allowed', _hover: { bg: 'gray.100' } }}
                    onClick={downloadPdf}
                    isDisabled={!canDownload}
                    isLoading={prepProgress}
                    loadingText="Preparing…"
                  >
                    Download PDF
                  </Button>
                </Box>
              </Box>
            </MotionFlex>
          )}
        </VStack>
      </Box>

      <SupportedFormatsSection />
    </Flex>
  );
};

export default ConvertToPdfPage;
